import { useSuspenseQuery } from '@apollo/client';
import { useMemo } from 'react';

import { ClickReasonsEnum } from '@xing-com/crate-common-graphql-types';
import {
  NEW_COMPANY_URL,
  PAGES_PATH,
} from '@xing-com/crate-entity-pages-entity-pages-shared-urls';

import { EntityPageDocument } from '../../graphql/queries/entity-page-query.gql-types';
import type { EntityPageData } from '../../types/entity-page';
import type { HeaderModuleData } from '../../types/header-module';

type EntityPageHookResult =
  | {
      entityPage: EntityPageData;
      headerModule?: HeaderModuleData;
      redirectTo: undefined;
      errorCode: undefined;
    }
  | {
      entityPage: undefined;
      headerModule: undefined;
      redirectTo: string;
      errorCode: undefined;
    }
  | {
      entityPage: undefined;
      headerModule: undefined;
      redirectTo: undefined;
      errorCode: 404 | 500 | 503;
    };

type Options = {
  pageSlug: string;
};
export const useEntityPage = ({ pageSlug }: Options): EntityPageHookResult => {
  const variables = useMemo(
    () => ({
      id: pageSlug,
      socialProofClickReasonsKey:
        ClickReasonsEnum.CrWebPublisherSocialProofHeader,
    }),
    [pageSlug]
  );
  const { data, error } = useSuspenseQuery(EntityPageDocument, {
    variables,
    errorPolicy: 'all',
  });

  return useMemo((): EntityPageHookResult => {
    const entityPage = data?.entityPageEX;

    if (error || entityPage?.__typename !== 'EntityPage') {
      switch (entityPage?.__typename) {
        case 'EntityPageError':
          return {
            entityPage: undefined,
            headerModule: undefined,
            redirectTo: undefined,
            errorCode: entityPage.errorCode === 10404 ? 404 : 503,
          };

        case 'EntityPageMoved':
          return {
            entityPage: undefined,
            headerModule: undefined,
            redirectTo: PAGES_PATH(entityPage.slug),
            errorCode: undefined,
          };

        default:
          return {
            entityPage: undefined,
            headerModule: undefined,
            redirectTo: undefined,
            errorCode: 503,
          };
      }
    }

    if (entityPage.publicationStatus === 'CREATED') {
      return {
        entityPage: undefined,
        headerModule: undefined,
        redirectTo: NEW_COMPANY_URL,
        errorCode: undefined,
      };
    }

    const headerModule =
      data?.headerModule?.__typename === 'EntityPageHeaderModule'
        ? data.headerModule
        : undefined;

    return {
      entityPage,
      headerModule,
      errorCode: undefined,
      redirectTo: undefined,
    };
  }, [data, error, variables]);
};
